import { useEffect, useState } from 'react'
import { /* Link */ useLocation } from 'react-router'
import {
  House,
  Settings,
  LogOut,
  Menu,
  X,
  Users,
  UserStar,
  Bell,
  MessageCircleWarning,
  Smartphone
} from 'lucide-react'
import classNames from 'classnames'

import Flex from '@/elements/Flex'
import Avatar from '@/elements/Avatar'
import Brand from '@/elements/Brand'
import Button from '@/elements/Button'
import NavLink from '@/elements/NavLink'
import Text from '@/elements/Text'
import Label from '@/elements/Label'
import Pill from '@/elements/Pill'
import FormSelect from '@/elements/FormSelect'
import GetTheAppModal from '@/components/GetTheAppModal'
import useAuthStore from '@/stores/authStore'
import useBreakpoints from '@/hooks/useBreakpoints'
import useRoles from '@/hooks/useRoles'
import type { MeResponse } from '@/api/types/User'
import { useGetUnreadNotificationsCount } from '@/api/notifications'
import { colors } from '@/utils/theme'

import './Sidebar.scss'

function Sidebar() {
  const {
    user,
    organizations,
    currentOrganization,
    hasOrganizations,
    changeOrganization
  } = useAuthStore()
  const {
    roleName,
    isAdmin,
    isDelegate,
    isSubject,
    isSuperAdmin,
    getRoleColor,
    getRoleName
  } = useRoles()
  const { isMobile, isTablet } = useBreakpoints()
  const location = useLocation()
  const [showSidebar, setShowSidebar] = useState(false)
  const [showGetTheAppModal, setShowGetTheAppModal] = useState(false)
  const [scrollStart, setScrollStart] = useState(false)

  const appVersion = APP_VERSION

  const handleToggleSidebar = () => {
    setShowSidebar(state => !state)
  }

  const { data: unreadData } = useGetUnreadNotificationsCount()
  const unreadCount = unreadData?.data || 0

  useEffect(() => {
    const container = document.querySelector('.container > section')
    if (!container) return

    const handleScroll = () => {
      setScrollStart(container.scrollTop > 0)
    }

    container.addEventListener('scroll', handleScroll)
    setScrollStart(container.scrollTop > 0)

    // eslint-disable-next-line consistent-return
    return () => container.removeEventListener('scroll', handleScroll)
  }, [location.pathname])

  const renderNavLinks = (onClick: () => void) => (
    <Flex wfull grow gap={3} justify='space-between' className='nav-links'>
      <Flex wfull gap={3}>
        <Flex wfull component='nav' gap={2}>
          <Text variant='footnote' color='black-light-40'>Navigation</Text>
          <Flex gap={1}>
            <NavLink
              to='/'
              onClick={onClick}
              text={isSubject ? 'Watchdog' : 'Dashboard'}
              Icon={House}
            />
            {(isAdmin || isDelegate) && (
              <NavLink
                to='/my-alerts'
                onClick={onClick}
                text='My Alerts'
                Icon={MessageCircleWarning}
              />
            )}
            {isAdmin && (
              <NavLink
                to='/organization'
                onClick={onClick}
                text='Organization'
                Icon={Users}
              />
            )}
          </Flex>
        </Flex>
        <Flex wfull component='nav' gap={2}>
          <Text variant='footnote' color='black-light-40'>General</Text>
          <Flex gap={1}>
            <NavLink
              to='/notifications'
              onClick={onClick}
              text={unreadCount > 0 ? `Notifications (${unreadCount})` : 'Notifications'}
              Icon={Bell}
            />
            <NavLink
              to='/settings'
              onClick={onClick}
              text='Settings'
              Icon={Settings}
            />
          </Flex>
        </Flex>
      </Flex>

      <Flex wfull component='nav' gap={2}>
        <Text variant='footnote' color='black-light-40'>Other</Text>
        <Flex gap={1}>
          <>
            {isSuperAdmin && (
              <NavLink
                noPropagate
                onClick={async () => {

                  try {
                    const requestHeaders: HeadersInit = new Headers()
                    requestHeaders.set('Accept', 'application/json')

                    const authStoreData = useAuthStore.getState()
                    const tokenData = authStoreData.credentials
                    if (tokenData) {
                      requestHeaders.set(
                        'Authorization',
                        `${tokenData.token_type} ${tokenData.access_token}`
                      )
                    }
                    const req = await fetch('/superadmin/access', {
                      method: 'POST',
                      headers: requestHeaders
                    })

                    await fetch('/superadmin', {
                      method: 'GET',
                      headers: requestHeaders,
                    })

                    if (req.ok) {
                      window.location.pathname = '/superadmin/'
                    }

                  } catch {
                    window.location.pathname = '/'
                  }
                }}
                text='Admin Panel'
                Icon={UserStar}
              />
            )}
            <NavLink
              onClick={() => setShowGetTheAppModal(true)}
              text='Our Products'
              Icon={Smartphone}
              noPropagate
            />
            <GetTheAppModal isOpen={showGetTheAppModal} onClose={() => setShowGetTheAppModal(false)} />
          </>
          <NavLink
            onClick={onClick}
            text='Log Out'
            Icon={LogOut}
            isLogout
          />
        </Flex>
      </Flex>

      <Flex wfull gap={4}>
        <Flex wfull color='gray' style={{ height: 1 }} />
        {hasOrganizations && (
          <Flex wfull style={{ position: 'relative' }}>
            <FormSelect<MeResponse['organizations'][number]>
              optionsAutoWidth
              name='organization'
              placeholder='Select Organization'
              noVerticalScroll
              disabled={(organizations?.length || 0) < 2}
              options={organizations || []}
              values={currentOrganization ? [currentOrganization] : []}
              optionValue={item => item.id}
              optionLabel={(item, selected) => (selected ? item.name : (
                <Flex gap={2}>
                  <Text>
                    {item.name}
                  </Text>
                  {item.roles[0] && (
                    <Pill customColor={getRoleColor(item.roles[0])}>
                      {getRoleName(item.roles[0])}
                    </Pill>
                  )}
                </Flex>
              ))}
              onChange={([v]) => v && changeOrganization(v.id)}
              style={{ width: '100%' }}
            />
          </Flex>
        )}
        <Flex inline align='center' gap={3}>
          <Avatar
            name={user?.details?.fullName || ''}
            src={user?.assets?.PROFILE_PICTURE || undefined}
            size='small'
            isMember
          />
          <Flex>
            <Text variant='h4' color='black'>{user?.details?.fullName}</Text>
            {isSubject ? null : (
              <Label position='left'>{roleName}</Label>
            )}
          </Flex>
        </Flex>
      </Flex>
      <Text variant='footnote' color='black-light-80' className='app-version'>v{appVersion}</Text>
    </Flex>
  )

  if (isTablet) {
    return (
      <Flex
        wfull
        component='aside'
        justify='center'
        gap={1}
        className={classNames('sidebar compressed', { 'scrolled': scrollStart })}
      >
        <Brand />
        <Button
          variant='iconBtn'
          className='toggle'
          onClick={handleToggleSidebar}
          styles={{ border: 'none', backgroundColor: 'transparent' }}
        >
          {isMobile && showSidebar ? (
            <X size={40} color={colors['black']} />
          ) : (
            <Menu size={40} color={colors['black']} />
          )}
        </Button>
        <Flex className={classNames('mobile-sidebar', { 'show': showSidebar })}>
          <Flex gap={6} className='sidebar collapsable'>
            {!isMobile && (
              <>
                <Brand />
                <Button
                  variant='iconBtn'
                  className='toggle'
                  onClick={handleToggleSidebar}
                  styles={{ border: 'none', backgroundColor: 'transparent' }}
                >
                  <X size={40} color={colors['black']} />
                </Button>
              </>
            )}
            {renderNavLinks(handleToggleSidebar)}
          </Flex>
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex component='aside' gap={4} className='sidebar'>
      <Brand />
      {renderNavLinks(handleToggleSidebar)}
    </Flex>
  )
}

export default Sidebar
